import React from 'react';
import { POSProduct } from '../../pages/pos/POSTerminal';

interface Props {
  products: POSProduct[];
  loading: boolean;
  onAddProduct: (product: POSProduct) => void;
}

const POSProductGrid: React.FC<Props> = ({ products, loading, onAddProduct }) => {
  const getImageUrl = (image: string | null) => {
    if (!image) return null;
    if (image.startsWith('http')) return image;
    const baseUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || '';
    return `${baseUrl}/storage/${image}`;
  };

  if (loading && products.length === 0) {
    return (
      <div className="flex-1 overflow-y-auto p-3 grid grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2 auto-rows-min">
        {Array.from({ length: 15 }).map((_, i) => (
          <div key={i} className="bg-gray-700 rounded-lg p-2 animate-pulse">
            <div className="aspect-square bg-gray-600 rounded-md mb-2" />
            <div className="h-3 bg-gray-600 rounded w-3/4 mb-1" />
            <div className="h-3 bg-gray-600 rounded w-1/2" />
          </div>
        ))}
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-gray-400">No products found</div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto p-3 grid grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2 auto-rows-min">
      {products.map((product) => (
        <button
          key={product.id}
          onClick={() => onAddProduct(product)}
          disabled={product.total_stock <= 0}
          className={`bg-gray-700 rounded-lg p-2 text-left transition hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 relative ${
            product.total_stock <= 0 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
          }`}
        >
          {/* Image */}
          <div className="aspect-square bg-gray-600 rounded-md overflow-hidden mb-2">
            {product.image ? (
              <img
                src={getImageUrl(product.image) || undefined}
                alt={product.name}
                className="w-full h-full object-cover"
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-500 text-xs">
                No Image
              </div>
            )}
          </div>

          {/* Info */}
          <div className="space-y-0.5">
            <p className="text-xs text-white font-medium truncate">{product.name}</p>
            <p className="text-xs text-gray-400 truncate">{product.product_code}</p>
            <div className="flex justify-between items-center">
              <div>
                {product.has_promotion ? (
                  <>
                    <p className="text-sm font-bold text-green-400">
                      {Number(product.selling_price).toLocaleString()} MMK
                    </p>
                    <p className="text-xs text-gray-500 line-through">
                      {Number(product.price).toLocaleString()}
                    </p>
                  </>
                ) : (
                  <p className="text-sm font-bold text-green-400">
                    {Number(product.price).toLocaleString()} MMK
                  </p>
                )}
              </div>
              <span className={`text-xs px-1.5 py-0.5 rounded ${
                product.total_stock > 0 ? 'bg-green-900 text-green-300' : 'bg-red-900 text-red-300'
              }`}>
                {product.total_stock}
              </span>
            </div>
          </div>

          {/* Out of stock overlay */}
          {product.total_stock <= 0 && (
            <div className="absolute inset-0 bg-black bg-opacity-50 rounded-lg flex items-center justify-center">
              <span className="text-red-400 text-xs font-bold">OUT OF STOCK</span>
            </div>
          )}
        </button>
      ))}
    </div>
  );
};

export default POSProductGrid;
